import { useNavigation, useRoute } from '@react-navigation/native';
import {
  createStackNavigator,
  StackScreenProps,
} from '@react-navigation/stack';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { InferType } from 'yup';

import { Alert } from '@components/Alert';
import SecondaryHeader from '@components/Headers/SecondaryHeader';
import { SimpleHeader } from '@components/Headers/SimpleHeader.web';
import { AddMemberType } from '@components/Invite/SelectMembersList';
import { Box } from '@components/Restyle';
import {
  useCreateTeamMemberInvitationMutation,
  useUpdateTeamMembersMutation,
  TeamRole,
  User,
  GetTeamDocument,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { TeamsStackScreenProps } from '@navigation/teams/teams-stack';
import addMembersSchema from '@schemas/addMembersSchema';
import { AddMembers } from '@screens/Members/AddMembers';
import { EditNewMemberRoles } from '@screens/Members/EditNewMemberRoles';
import { SelectMembersFromTeam } from '@screens/Members/SelectMembersFromTeam';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import theme from '@themes/theme';
import {
  defaultTeamRole,
  selectableTeamRoles,
  TEAM_ROLE_DESCRIPTIONS,
} from '@utils/teams';
import { splitTeamMemberChanges } from '@utils/teams/splitTeamMemberChanges';

export type FormValues = InferType<typeof addMembersSchema>;
type AddTeamMembersStackParamsList = {
  'add-members': undefined;
  'edit-new-member-roles': undefined;
  'select-members-from-team': undefined;
};

export type AddMembersStackScreenProps<
  T extends keyof AddTeamMembersStackParamsList,
> = StackScreenProps<AddTeamMembersStackParamsList, T>;

const Stack = createStackNavigator<AddTeamMembersStackParamsList>();

const createAddMembersHeader =
  (onClose?: () => void, hideClose?: boolean, isFromSubscription?: boolean) =>
  () =>
    isFromSubscription ? (
      <Box backgroundColor='white'>
        <SimpleHeader
          title='Add Members'
          onClose={onClose}
          hideClose={hideClose}
        />
      </Box>
    ) : (
      <SecondaryHeader title='Add Members' searchable={false} />
    );

const SelectMembersFromTeamHeader = () => <SimpleHeader title='Team' />;

const createAssignRoleHeader =
  (title: string, onClose?: () => void, hideClose?: boolean) => () =>
    Platform.OS === 'web' && onClose ? (
      <Box backgroundColor='white'>
        <SimpleHeader title={title} onClose={onClose} hideClose={hideClose} />
      </Box>
    ) : (
      <SecondaryHeader title={title} searchable={false} />
    );

interface AddTeamMembersStackProps {
  onClose?: () => void;
  isFromMyProfileWeb?: boolean;
  hideClose?: true;

  initialUsers?: User[];
  teamUUIDprop?: string;
  isFromSubscription?: boolean;
  onAddMembers?: (selected: User[]) => void;
  navigatedFromCreateTeam?: boolean;
  initialRouteName?: keyof AddTeamMembersStackParamsList;
}

export const AddTeamMembersStack: React.FC<AddTeamMembersStackProps> = ({
  onClose,
  isFromMyProfileWeb = false,
  isFromSubscription = false,
  initialUsers,
  onAddMembers,
  initialRouteName,
  navigatedFromCreateTeam = false,
  hideClose = false,
  teamUUIDprop,
}) => {
  const { t } = useTranslation();
  const [selected] = useState<AddMemberType[]>();
  const { trackEvent } = useMixpanel();
  const { me } = useMe();

  const { params: { teamUUId = '' } = {} } =
    useRoute<TeamsStackScreenProps<'add-team-members-stack'>['route']>();

  const navigation = useNavigation();

  const { team } = useTeamFromQuery({
    teamUUId: teamUUId ?? teamUUIDprop,
    includePending: true,
  });

  const refetchQueries =
    (teamUUId ?? teamUUIDprop)
      ? [
          {
            query: GetTeamDocument,
            variables: {
              id: teamUUId ?? teamUUIDprop,
              includePending: true,
            },
          },
        ]
      : [];

  const [updateTeamMembers] = useUpdateTeamMembersMutation({
    refetchQueries,
  });
  const [createTeamMemberInvitation] = useCreateTeamMemberInvitationMutation({
    refetchQueries,
  });

  const addMembersToTeam = async (values: FormValues) => {
    if (!team?.id) return;

    const existingMemberIds = team.users.map((u) => u.id);
    const { roleUpdates, invitations } = splitTeamMemberChanges(
      values.users,
      existingMemberIds
    );

    try {
      await Promise.all([
        roleUpdates.length &&
          updateTeamMembers({
            variables: { teamId: team.id, attributes: roleUpdates },
          }),
        invitations.length &&
          createTeamMemberInvitation({
            variables: { id: team.id, teamMembers: invitations },
          }),
      ]);
    } catch (error) {
      Alert.alert(t('shared:error'), (error as Error).message);
      return;
    }

    // Only users an invitation actually went to; one without an email is
    // dropped by splitTeamMemberChanges.
    values.users
      .filter((u) => !existingMemberIds.includes(u.id) && !!u.email?.trim())
      .forEach((invitedUser) => {
        trackEvent(MIXPANEL_EVENTS.TEAM_MEMBER_INVITED, {
          team_id: team.id,
          invited_user_id: invitedUser.id,
          role: invitedUser.role,
        });
        trackEvent(MIXPANEL_EVENTS.TEAM_INVITE_SENT, {
          team_id: team.id,
          inviter_user_id: me?.id,
          invite_method: 'email',
        });
      });
    onClose?.();
  };

  const submitForm = (values: FormValues) => {
    if (isFromSubscription) {
      onAddMembers?.(values.users);
      if (navigatedFromCreateTeam) {
        onClose?.();
        return;
      }
      navigation.goBack();
    } else {
      // Returned so Formik keeps isSubmitting set until the requests settle;
      // otherwise a second tap on Done sends the invitations again.
      return addMembersToTeam(values);
    }
  };

  const initialValues: FormValues = {
    users: isFromSubscription && initialUsers ? initialUsers : [],
    selectedUserIds: selected?.map((u) => u.id) || [],
  };
  const planPrice = team?.subscriptionDetails?.pricePerSeat
    ? (Number(team?.subscriptionDetails?.pricePerSeat) / 100).toFixed(0)
    : 0;

  const calculateStartingDate = () => {
    if (team?.subscriptionDetails?.subscriptionStatus === 'trialing') {
      const expirationDate = dayjs(team?.subscriptionDetails?.expirationDate);
      const updatedDate = expirationDate.add(1, 'second');
      return updatedDate.format('MMMM D');
    }

    const startingDate = dayjs();
    return startingDate.format('MMMM D');
  };
  const planStartingDate = calculateStartingDate();
  const upgradePricingInfo: string | undefined = team
    ? `Your subscription for ${team.name} is updated for adding new members. $${planPrice} ${team?.subscriptionDetails?.subscriptionInterval}/ per user starting ${planStartingDate}.`
    : undefined;

  return (
    <Box
      flex={1}
      mt={Platform.OS === 'web' && !isFromSubscription ? 'm' : 'none'}
      style={
        Platform.OS === 'web' && !isFromMyProfileWeb && !isFromSubscription
          ? { marginEnd: 300 + theme.spacing.m }
          : {}
      }>
      <Formik
        initialValues={initialValues}
        onSubmit={submitForm}
        validationSchema={addMembersSchema}
        validateOnMount>
        {({ setFieldValue, values, submitForm, isSubmitting }) => {
          const { selectedUserIds: selectedUserIdsRaw, users } = values;
          const selectedUserIds = selectedUserIdsRaw?.filter(
            (s) => !!s
          ) as string[];

          return (
            <Stack.Navigator initialRouteName={initialRouteName || undefined}>
              <Stack.Screen
                name='add-members'
                options={{
                  header: createAddMembersHeader(
                    onClose,
                    hideClose,
                    isFromSubscription
                  ),
                }}>
                {() => (
                  <AddMembers
                    hideTeamTab={true}
                    disabled={!users.length}
                    onNextPress={() =>
                      navigation.navigate('edit-new-member-roles')
                    }
                    showTaskTagSupportUser={false}
                    users={users}
                    updateUsers={(newUsers: AddMemberType[]) => {
                      setFieldValue(
                        'users',
                        newUsers.map((u) => {
                          return {
                            ...u,
                            role: u.role || defaultTeamRole,
                          };
                        })
                      );
                    }}
                  />
                )}
              </Stack.Screen>
              <Stack.Screen
                name='select-members-from-team'
                options={{
                  header: SelectMembersFromTeamHeader,
                }}>
                {() => (
                  <SelectMembersFromTeam
                    onSubmit={() =>
                      navigation.navigate('edit-new-member-roles')
                    }
                    disabled={!users.length}
                    selectedUserIds={selectedUserIds}
                    users={users}
                    updateUsers={(newUsers: AddMemberType[]) => {
                      setFieldValue(
                        'users',
                        newUsers.map((u) => {
                          return {
                            ...u,
                            role: u.role || defaultTeamRole,
                          };
                        })
                      );
                    }}
                  />
                )}
              </Stack.Screen>

              <Stack.Screen
                name='edit-new-member-roles'
                options={{
                  header: createAssignRoleHeader(
                    t('models:addMembers.assignRole'),
                    onClose,
                    hideClose
                  ),
                }}>
                {() => (
                  <EditNewMemberRoles<TeamRole>
                    roles={selectableTeamRoles}
                    roleDescriptions={TEAM_ROLE_DESCRIPTIONS}
                    updateMemberRole={(id: User['id'], role: TeamRole) => {
                      setFieldValue(
                        'users',
                        users.map((u) => {
                          return {
                            ...u,
                            role: u.id === id ? role : u.role,
                          };
                        })
                      );
                    }}
                    isTaskAssignee={false}
                    usersAndRoles={users.map((u) => {
                      return {
                        user: u,
                        role: u.role,
                      };
                    })}
                    goBack={true}
                    teamUUId={teamUUId}
                    onSubmit={submitForm}
                    isSubmitting={isSubmitting}
                    pricingInfo={upgradePricingInfo}
                  />
                )}
              </Stack.Screen>
            </Stack.Navigator>
          );
        }}
      </Formik>
    </Box>
  );
};
